import React, { useMemo } from 'react';
import { Box, costFormater, Text } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import usePlanOmniBalanceHistoryQuery from '@/modules/employer/components/BankingBalance/queries/usePlanOmniBalanceHistory.query';

const DATE_OF_MONTH_FORMAT = 'MMM DD';
const MAX_VISIBLE_LABELS = 6;

interface Params {
  accountId?: number;
}

const usePlanBalanceChartData = ({ accountId }: Params) => {
  const { data: rawData, isLoading } = usePlanOmniBalanceHistoryQuery(accountId);

  const values = useMemo(() => rawData.map((item) => item.available_balance || 0), [rawData]);

  const hints = useMemo(() => {
    return rawData.map((item) => {
      const date = dayjs(item.date);
      return (
        <Box
          round="container2Round"
          key={date.format()}
          background="background3"
          pad={{ vertical: 'xxxs', horizontal: 'xs' }}
        >
          <Text color="textOnColor" size="small" weight={400}>
            {date.format(DATE_OF_MONTH_FORMAT)}
          </Text>
          <Text color="textOnColor" size="small" weight={700}>
            {costFormater(item.available_balance, true)}
          </Text>
        </Box>
      );
    });
  }, [rawData]);

  const valueHints = useMemo(() => {
    return rawData.map((item) => {
      return costFormater(item.available_balance, true);
    });
  }, [rawData]);

  const labels = useMemo(() => {
    const len = rawData.length;
    if (!len) return [];

    if (len <= MAX_VISIBLE_LABELS) {
      return rawData.map((item) => dayjs(item.date).format(DATE_OF_MONTH_FORMAT));
    }

    // Pick MAX_VISIBLE_LABELS evenly spaced indices
    const visibleIndices = new Set(
      Array.from({ length: MAX_VISIBLE_LABELS }, (_, i) =>
        Math.round((i * (len - 1)) / (MAX_VISIBLE_LABELS - 1))
      )
    );

    return rawData.map((item, index) => {
      if (visibleIndices.has(index)) {
        return dayjs(item.date).format(DATE_OF_MONTH_FORMAT);
      }
      return null;
    });
  }, [rawData]);

  const isWithNegativeValues = useMemo(() => values.some((v) => v < 0), [values]);
  const isEmpty = useMemo(() => rawData.length === 0, [rawData]);

  return {
    values,
    hints,
    valueHints,
    labels,
    isLoading,
    isWithNegativeValues,
    isEmpty,
  };
};

export default usePlanBalanceChartData;
